/**
 * RibbonView — the drawing half of a TrailSystem ribbon (plan 16 §2.4):
 * a run of straight segments, a soft glow run under a core run.
 *
 * The Pixi impl (PixiRibbonView) draws each segment as a white sprite scaled
 * and rotated into place, so a moving ribbon is transform/tint/alpha writes
 * every frame — never a Graphics clear + redraw, which rebuilt the whole
 * stage. Unused segments sit at alpha 0. Wired at boot by
 * PixiFactoriesRegistry; TrailSystem tests inject their own.
 */

export type RibbonRun = 'glow' | 'core';

export interface RibbonViewStyle {
  depth: number;
  /** Attach to the bloom layer (additive) instead of the world layer. */
  glow: boolean;
}

export interface IRibbonView {
  /** Start a frame's segments (every run's cursor back to 0). */
  begin(): void;
  /** One straight segment with butt ends, centred on the line p1→p2. */
  segment(run: RibbonRun, x1: number, y1: number, x2: number, y2: number, width: number, color: number, alpha: number): void;
  /** A round cap (disc of diameter `width`) at an interior joint. */
  joint(run: RibbonRun, x: number, y: number, width: number, color: number, alpha: number): void;
  /** End the frame: segments and caps not drawn this frame go to alpha 0. */
  end(): void;
  /** Pool: hide by alpha (never `visible`) / bring back. */
  park(): void;
  unpark(): void;
  destroy(): void;
}

type Factory = (style: RibbonViewStyle) => IRibbonView;

let pixiFactory: Factory | null = null;

export function registerPixiRibbonViewFactory(fn: Factory): void {
  pixiFactory = fn;
}

export function createRibbonView(_scene: unknown, style: RibbonViewStyle): IRibbonView {
  if (!pixiFactory) {
    throw new Error('[createRibbonView] Pixi factory not registered. Call registerAllPixiFactories() at boot.');
  }
  return pixiFactory(style);
}
